import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Calendar, Download, Link2, Copy, CheckCircle2,
  Clock, Target, FileText, Users, Shield, RefreshCw, Trash2,
  AlertTriangle, Info, Bell
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compact export button for inline use
export const CalendarExportButton = ({ type, itemId, label = 'Add to Calendar', compact = false }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);

  const exportToCalendar = async () => {
    if (!token || !itemId) return;
    setLoading(true);
    
    try {
      let endpoint;
      switch (type) {
        case 'task':
          endpoint = `/api/calendar/export/task/${itemId}`;
          break;
        case 'followup':
          endpoint = `/api/calendar/export/followup/${itemId}`;
          break;
        case 'quote':
          endpoint = `/api/calendar/export/quote/${itemId}`;
          break;
        case 'enquiry':
          endpoint = `/api/calendar/export/enquiry/${itemId}`;
          break;
        default:
          toast.error('Unknown calendar export type');
          return;
      }

      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-${itemId}.ics`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Calendar event downloaded');
      } else {
        toast.error('Failed to export calendar event');
      }
    } catch (err) {
      console.error('Calendar export error:', err);
      toast.error('Error exporting to calendar');
    } finally {
      setLoading(false);
    }
  };

  if (compact) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); exportToCalendar(); }}
        disabled={loading}
        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-[#1e3a5f] transition-colors"
        title="Add to Calendar"
      >
        <Calendar className="h-4 w-4" />
      </button>
    );
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={exportToCalendar}
      disabled={loading}
      className="text-slate-600 hover:text-[#1e3a5f]"
    >
      <Calendar className="h-4 w-4 mr-1" />
      {loading ? 'Exporting...' : label}
    </Button>
  );
};

export const CalendarSubscriptionDialog = ({ open, onOpenChange }) => {
  const { token, user } = useAuth();
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [calendarSettings, setCalendarSettings] = useState(null);
  const [activeTab, setActiveTab] = useState('download'); // 'download', 'subscribe', or 'preferences'
  const [options, setOptions] = useState({
    tasks: true,
    followups: true,
    quotes: true,
    enquiries: true,
    approvals: true,
    days: 30
  });
  const [notificationPrefs, setNotificationPrefs] = useState({
    include_tasks: true,
    include_followups: true,
    include_quotes: true,
    include_enquiries: true,
    include_approvals: true,
    days_ahead: 30,
    reminder_minutes_tasks: 30,
    reminder_minutes_followups: 15,
    reminder_minutes_deadlines: 1440,
    reminder_minutes_approvals: 480,
    reminder_minutes_milestones: 1440
  });
  const [savingPrefs, setSavingPrefs] = useState(false);

  const isDirector = user?.role === 'director';

  const fetchSubscriptionInfo = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/subscription/info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscriptionInfo(data.info);
      }
    } catch (err) {
      console.error('Error fetching subscription info:', err);
    }
  }, [token]);

  const fetchCalendarSettings = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCalendarSettings(data.settings);
      }
    } catch (err) {
      console.error('Error fetching calendar settings:', err);
    }
  }, [token]);

  const fetchNotificationPrefs = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/notification-preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setNotificationPrefs(data.preferences);
      }
    } catch (err) {
      console.error('Error fetching notification preferences:', err);
    }
  }, [token]);

  useEffect(() => {
    if (open && token) {
      fetchSubscriptionInfo();
      fetchCalendarSettings();
      fetchNotificationPrefs();
    }
  }, [open, token, fetchSubscriptionInfo, fetchCalendarSettings, fetchNotificationPrefs]);

  const updateCalendarSettings = async (updates) => {
    if (!isDirector) return;
    try {
      const res = await fetch(`${API_URL}/api/calendar/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      if (res.ok) {
        const data = await res.json();
        setCalendarSettings(data.settings);
        toast.success('Calendar settings updated');
      }
    } catch (err) {
      toast.error('Error updating settings');
    }
  };

  const saveNotificationPrefs = async () => {
    setSavingPrefs(true);
    try {
      const res = await fetch(`${API_URL}/api/calendar/notification-preferences`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationPrefs)
      });
      if (res.ok) {
        toast.success('Notification preferences saved');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (err) {
      toast.error('Error saving preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const generateSubscription = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/subscription`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSubscriptionUrl(data.subscription_url);
        fetchSubscriptionInfo();
        toast.success('Subscription URL generated');
      } else {
        toast.error('Failed to generate subscription');
      }
    } catch (err) {
      toast.error('Error generating subscription');
    } finally {
      setLoading(false);
    }
  };

  const rotateSubscription = async () => {
    if (!token) return;
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/subscription/rotate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setSubscriptionUrl(data.subscription_url);
        fetchSubscriptionInfo();
        toast.success('Subscription URL rotated. Old URL no longer works.');
      } else {
        toast.error('Failed to rotate subscription');
      }
    } catch (err) {
      toast.error('Error rotating subscription');
    } finally {
      setLoading(false);
    }
  };

  const revokeSubscription = async () => {
    if (!token) return;
    
    try {
      const res = await fetch(`${API_URL}/api/calendar/subscription/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSubscriptionUrl('');
        setSubscriptionInfo({ has_active_token: false });
        toast.success('Subscription revoked');
      } else {
        toast.error('Failed to revoke subscription');
      }
    } catch (err) {
      toast.error('Error revoking subscription');
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(subscriptionUrl);
      setCopied(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const downloadFeed = async () => {
    if (!token) return;
    
    try {
      const params = new URLSearchParams({
        days: options.days.toString(),
        tasks: options.tasks.toString(),
        followups: options.followups.toString(),
        quotes: options.quotes.toString(),
        enquiries: options.enquiries.toString(),
        approvals: options.approvals.toString()
      });

      const res = await fetch(`${API_URL}/api/calendar/feed?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'opsflow-calendar.ics';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success('Calendar downloaded');
      } else {
        toast.error('Failed to download calendar');
      }
    } catch (err) {
      toast.error('Error downloading calendar');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-md p-4 sm:p-6">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Calendar className="h-5 w-5 text-[#1e3a5f]" />
            Calendar Integration
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            Export OpsFlow events to your calendar
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Security Badge */}
          <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg border border-emerald-100">
            <Shield className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <span className="text-xs text-emerald-700">One-way export only. OpsFlow never reads your calendars.</span>
          </div>

          {/* Tab Selection */}
          <div className="flex rounded-xl bg-slate-100 p-1">
            <button
              onClick={() => setActiveTab('download')}
              className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'download' 
                  ? 'bg-white text-[#1e3a5f] shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Download className="h-3.5 w-3.5 inline mr-1" />
              Download
            </button>
            <button
              onClick={() => setActiveTab('subscribe')}
              className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'subscribe' 
                  ? 'bg-white text-[#1e3a5f] shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Link2 className="h-3.5 w-3.5 inline mr-1" />
              Subscribe
            </button>
            <button
              onClick={() => setActiveTab('preferences')}
              className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'preferences' 
                  ? 'bg-white text-[#1e3a5f] shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <Bell className="h-3.5 w-3.5 inline mr-1" />
              Alerts
            </button>
          </div>

          {/* Download Tab */}
          {activeTab === 'download' && (
            <div className="space-y-4">
              {/* Event Types */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-slate-600">Include Events</Label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setOptions({ ...options, tasks: !options.tasks })}
                    className={`p-2.5 rounded-xl border text-center transition-all ${
                      options.tasks 
                        ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' 
                        : 'border-slate-200 text-slate-500'
                    }`}
                  >
                    <Target className="h-4 w-4 mx-auto mb-1" />
                    <span className="text-xs">Tasks</span>
                  </button>
                  <button
                    onClick={() => setOptions({ ...options, followups: !options.followups })}
                    className={`p-2.5 rounded-xl border text-center transition-all ${
                      options.followups 
                        ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' 
                        : 'border-slate-200 text-slate-500'
                    }`}
                  >
                    <Users className="h-4 w-4 mx-auto mb-1" />
                    <span className="text-xs">Follow-ups</span>
                  </button>
                  <button
                    onClick={() => setOptions({ ...options, quotes: !options.quotes })}
                    className={`p-2.5 rounded-xl border text-center transition-all ${
                      options.quotes 
                        ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' 
                        : 'border-slate-200 text-slate-500'
                    }`}
                  >
                    <FileText className="h-4 w-4 mx-auto mb-1" />
                    <span className="text-xs">Quotes</span>
                  </button>
                  <button
                    onClick={() => setOptions({ ...options, enquiries: !options.enquiries })}
                    className={`p-2.5 rounded-xl border text-center transition-all ${
                      options.enquiries 
                        ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' 
                        : 'border-slate-200 text-slate-500'
                    }`}
                  >
                    <Calendar className="h-4 w-4 mx-auto mb-1" />
                    <span className="text-xs">Projects</span>
                  </button>
                  <button
                    onClick={() => setOptions({ ...options, approvals: !options.approvals })}
                    className={`p-2.5 rounded-xl border text-center transition-all ${
                      options.approvals 
                        ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]' 
                        : 'border-slate-200 text-slate-500'
                    }`}
                  >
                    <CheckCircle2 className="h-4 w-4 mx-auto mb-1" />
                    <span className="text-xs">Approvals</span>
                  </button>
                </div>
              </div>

              {/* Days Ahead */}
              <div className="flex items-center justify-between">
                <Label className="text-xs font-medium text-slate-600">Days ahead</Label>
                <div className="flex items-center gap-2">
                  {[7, 14, 30, 60].map(d => (
                    <button
                      key={d}
                      onClick={() => setOptions({ ...options, days: d })}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                        options.days === d 
                          ? 'bg-[#1e3a5f] text-white' 
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Download Button */}
              <Button
                onClick={downloadFeed}
                className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl h-11"
              >
                <Download className="h-4 w-4 mr-2" />
                Download .ics File
              </Button>

              <p className="text-xs text-slate-400 text-center">
                Import the downloaded file into any calendar app
              </p>
            </div>
          )}

          {/* Subscribe Tab */}
          {activeTab === 'subscribe' && (
            <div className="space-y-4">
              {/* Privacy Settings (Director only) */}
              {isDirector && calendarSettings && (
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-700">Include project details</p>
                    <p className="text-xs text-slate-500 truncate">Show client/project names in events</p>
                  </div>
                  <Switch
                    checked={calendarSettings.include_details}
                    onCheckedChange={(checked) => updateCalendarSettings({ include_details: checked })}
                  />
                </div>
              )}

              {subscriptionInfo?.has_active_token || subscriptionUrl ? (
                <div className="space-y-3">
                  {/* Subscription URL */}
                  {subscriptionUrl && (
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-slate-600">Your feed URL</Label>
                      <div className="flex gap-2">
                        <Input
                          value={subscriptionUrl}
                          readOnly
                          className="text-xs bg-slate-50 h-9"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyToClipboard}
                          className="flex-shrink-0 h-9 w-9 p-0"
                        >
                          {copied ? (
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Quick Add Buttons */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-slate-600">Add to calendar</Label>
                    <div className="grid grid-cols-3 gap-2">
                      <a
                        href={`https://calendar.google.com/calendar/r?cid=${encodeURIComponent(subscriptionUrl || '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex flex-col items-center gap-1 p-3 bg-white rounded-xl border border-slate-200 hover:border-[#1e3a5f] hover:bg-slate-50 transition-all ${!subscriptionUrl ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <span className="text-lg">📅</span>
                        <span className="text-xs font-medium">Google</span>
                      </a>
                      <a
                        href={subscriptionUrl ? `webcal://${subscriptionUrl.replace(/^https?:\/\//, '')}` : '#'}
                        className={`flex flex-col items-center gap-1 p-3 bg-white rounded-xl border border-slate-200 hover:border-[#1e3a5f] hover:bg-slate-50 transition-all ${!subscriptionUrl ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <span className="text-lg">🍎</span>
                        <span className="text-xs font-medium">Apple</span>
                      </a>
                      <a
                        href={subscriptionUrl ? `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(subscriptionUrl)}` : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex flex-col items-center gap-1 p-3 bg-white rounded-xl border border-slate-200 hover:border-[#1e3a5f] hover:bg-slate-50 transition-all ${!subscriptionUrl ? 'opacity-50 pointer-events-none' : ''}`}
                      >
                        <span className="text-lg">📧</span>
                        <span className="text-xs font-medium">Outlook</span>
                      </a>
                    </div>
                  </div>

                  {/* Subscription Info */}
                  {subscriptionInfo?.has_active_token && (
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <p className="text-xs text-slate-500">Created</p>
                          <p className="text-xs font-medium">{subscriptionInfo.created_at?.split('T')[0]}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Last used</p>
                          <p className="text-xs font-medium">{subscriptionInfo.last_accessed?.split('T')[0] || 'Never'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Syncs</p>
                          <p className="text-xs font-medium">{subscriptionInfo.access_count || 0}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Management Actions */}
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={rotateSubscription}
                      disabled={loading}
                      className="flex-1 h-9 text-xs"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                      New URL
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={revokeSubscription}
                      className="flex-1 h-9 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      Revoke
                    </Button>
                  </div>

                  <p className="text-xs text-amber-600 flex items-start gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    Keep this URL private. Use &quot;New URL&quot; if compromised.
                  </p>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Link2 className="h-6 w-6 text-blue-500" />
                  </div>
                  <h4 className="font-medium text-slate-700 mb-1">Auto-sync your calendar</h4>
                  <p className="text-xs text-slate-500 mb-4">
                    Generate a subscription URL that keeps your calendar updated automatically
                  </p>
                  <Button
                    onClick={generateSubscription}
                    disabled={loading}
                    className="bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl"
                  >
                    {loading ? 'Generating...' : 'Generate Subscription URL'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Preferences Tab */}
          {activeTab === 'preferences' && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700">
                    These preferences control which events appear in your calendar subscription feed.
                  </p>
                </div>
              </div>

              {/* Event Types to Include */}
              <div className="space-y-3">
                <Label className="text-xs font-medium text-slate-600">Events to include</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4 text-amber-500" />
                      <span className="text-sm text-slate-700">Tasks</span>
                    </div>
                    <Switch
                      checked={notificationPrefs.include_tasks}
                      onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, include_tasks: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4 text-violet-500" />
                      <span className="text-sm text-slate-700">Follow-ups</span>
                    </div>
                    <Switch
                      checked={notificationPrefs.include_followups}
                      onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, include_followups: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-red-500" />
                      <span className="text-sm text-slate-700">Quote deadlines</span>
                    </div>
                    <Switch
                      checked={notificationPrefs.include_quotes}
                      onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, include_quotes: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-pink-500" />
                      <span className="text-sm text-slate-700">Project milestones</span>
                    </div>
                    <Switch
                      checked={notificationPrefs.include_enquiries}
                      onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, include_enquiries: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-500" />
                      <span className="text-sm text-slate-700">Approval requests</span>
                    </div>
                    <Switch
                      checked={notificationPrefs.include_approvals}
                      onCheckedChange={(checked) => setNotificationPrefs({ ...notificationPrefs, include_approvals: checked })}
                    />
                  </div>
                </div>
              </div>

              {/* Reminder Timings */}
              <div className="space-y-3">
                <Label className="text-xs font-medium text-slate-600">Reminder timing (0 = no reminder)</Label>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-xs text-slate-600">Tasks</span>
                    <div className="flex items-center gap-2">
                      {[0, 15, 30, 60].map(mins => (
                        <button
                          key={mins}
                          onClick={() => setNotificationPrefs({ ...notificationPrefs, reminder_minutes_tasks: mins })}
                          className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                            notificationPrefs.reminder_minutes_tasks === mins 
                              ? 'bg-[#1e3a5f] text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {mins === 0 ? 'Off' : `${mins}m`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-xs text-slate-600">Follow-ups</span>
                    <div className="flex items-center gap-2">
                      {[0, 5, 15, 30].map(mins => (
                        <button
                          key={mins}
                          onClick={() => setNotificationPrefs({ ...notificationPrefs, reminder_minutes_followups: mins })}
                          className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                            notificationPrefs.reminder_minutes_followups === mins 
                              ? 'bg-[#1e3a5f] text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {mins === 0 ? 'Off' : `${mins}m`}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-xs text-slate-600">Quote Deadlines</span>
                    <div className="flex items-center gap-2">
                      {[{ v: 0, l: 'Off' }, { v: 60, l: '1h' }, { v: 1440, l: '1d' }, { v: 2880, l: '2d' }].map(opt => (
                        <button
                          key={opt.v}
                          onClick={() => setNotificationPrefs({ ...notificationPrefs, reminder_minutes_deadlines: opt.v })}
                          className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                            notificationPrefs.reminder_minutes_deadlines === opt.v 
                              ? 'bg-[#1e3a5f] text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-xs text-slate-600">Approvals</span>
                    <div className="flex items-center gap-2">
                      {[{ v: 0, l: 'Off' }, { v: 240, l: '4h' }, { v: 480, l: '8h' }, { v: 1440, l: '1d' }].map(opt => (
                        <button
                          key={opt.v}
                          onClick={() => setNotificationPrefs({ ...notificationPrefs, reminder_minutes_approvals: opt.v })}
                          className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                            notificationPrefs.reminder_minutes_approvals === opt.v 
                              ? 'bg-[#1e3a5f] text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <span className="text-xs text-slate-600">Milestones</span>
                    <div className="flex items-center gap-2">
                      {[{ v: 0, l: 'Off' }, { v: 1440, l: '1d' }, { v: 2880, l: '2d' }, { v: 4320, l: '3d' }].map(opt => (
                        <button
                          key={opt.v}
                          onClick={() => setNotificationPrefs({ ...notificationPrefs, reminder_minutes_milestones: opt.v })}
                          className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                            notificationPrefs.reminder_minutes_milestones === opt.v 
                              ? 'bg-[#1e3a5f] text-white' 
                              : 'bg-white text-slate-600 hover:bg-slate-200'
                          }`}
                        >
                          {opt.l}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Days Ahead */}
              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <span className="text-xs text-slate-600">Days to look ahead</span>
                <div className="flex items-center gap-2">
                  {[14, 30, 60, 90].map(d => (
                    <button
                      key={d}
                      onClick={() => setNotificationPrefs({ ...notificationPrefs, days_ahead: d })}
                      className={`px-2 py-1 rounded-lg text-xs font-medium transition-all ${
                        notificationPrefs.days_ahead === d 
                          ? 'bg-[#1e3a5f] text-white' 
                          : 'bg-white text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </div>

              {/* Save Button */}
              <Button
                onClick={saveNotificationPrefs}
                disabled={savingPrefs}
                className="w-full bg-[#1e3a5f] hover:bg-[#162c46] text-white rounded-xl h-11"
              >
                {savingPrefs ? 'Saving...' : 'Save Preferences'}
              </Button>
            </div>
          )}

          {/* Footer */}
          <div className="pt-2 border-t border-slate-100">
            <p className="text-xs text-slate-400 text-center flex items-center justify-center gap-1">
              <Clock className="h-3 w-3" />
              Subscriptions sync when your calendar refreshes
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalendarExportButton;
